import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, FilterChips } from "@/components/app/PageBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtINR, fmtDate, downloadReceiptPdf, parseBlobError } from "@/lib/api";
import { Search, FileDown, Mail, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABEL = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank transfer",
  upi: "UPI",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Payments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ paid_at: "", method: "bank_transfer", reference_no: "", notes: "" });
  const [editBusy, setEditBusy] = useState(false);

  const [delOpen, setDelOpen] = useState(false);
  const [delFor, setDelFor] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = q.trim() ? { q: q.trim() } : {};
      const r = await api.get("/payments", { params });
      setList(r.data);
    } catch (e) {
      toast.error("Failed to load payments");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (q.trim()) next.set("q", q.trim()); else next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const total = list.reduce((s, p) => s + Number(p.amount || 0), 0);
  const [method, setMethod] = useState("all");
  const shown = method === "all" ? list : list.filter((p) => p.method === method);
  const methodCount = (m) => list.filter((p) => p.method === m).length;

  const onDownloadReceipt = async (p) => {
    setBusyId(p.id);
    try { await downloadReceiptPdf(p); }
    catch (e) { toast.error(await parseBlobError(e)); }
    finally { setBusyId(null); }
  };
  const onEmailReceipt = async (p) => {
    setBusyId(p.id);
    try {
      const r = await api.post(`/payments/${p.id}/resend-receipt`);
      toast.success(`Receipt emailed to ${r.data?.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setBusyId(null); }
  };

  const openEdit = (p) => {
    setEditFor(p);
    setEditForm({
      paid_at: (p.paid_at || "").slice(0, 10) || todayISO(),
      method: p.method || "bank_transfer",
      reference_no: p.reference_no || "",
      notes: p.notes || "",
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    setEditBusy(true);
    try {
      await api.patch(`/payments/${editFor.id}`, editForm);
      toast.success("Payment updated");
      setEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setEditBusy(false); }
  };

  const openDelete = (p) => { setDelFor(p); setDelOpen(true); };
  const submitDelete = async () => {
    setDelBusy(true);
    try {
      const r = await api.delete(`/payments/${delFor.id}`);
      if (r.data?.credit_warning) {
        toast.warning(r.data.credit_warning, { duration: 10000 });
      } else {
        toast.success("Payment deleted & invoice balance restored");
      }
      setDelOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDelBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle={loading ? "Loading payments…" : `${list.length} payment${list.length===1?"":"s"} · ${fmtINR(total)} total`}
        actions={
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none"/>
            <Input data-testid="pay-search" placeholder="Search name / plot / invoice / email / phone / ref"
              value={q} onChange={(e)=>setQ(e.target.value)}
              className="pl-8 w-96"/>
          </div>
        }
      />
      <FilterChips testId="pay-chips" value={method} onChange={setMethod} options={[
        { value: "all", label: "All", count: list.length },
        { value: "upi", label: "UPI", count: methodCount("upi") },
        { value: "cash", label: "Cash", count: methodCount("cash") },
        { value: "bank_transfer", label: "Bank transfer", count: methodCount("bank_transfer") },
        { value: "cheque", label: "Cheque", count: methodCount("cheque") },
      ]}/>
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Invoice #</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Plot</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Customer</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Method</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Reference</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Amount</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Actions</th>
          </tr></thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.id} data-testid={`pay-row-${p.id}`} className="border-t border-border hover:bg-muted/40">
                <td className="px-4 py-3 tabular text-muted-foreground">{fmtDate(p.paid_at || p.created_at)}</td>
                <td className="px-4 py-3 tabular font-medium">{p.invoice_no || "-"}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.plot_no || "-"}</td>
                <td className="px-4 py-3">
                  <div>{p.customer_name || "-"}</div>
                  {(p.customer_email || p.customer_phone) && (
                    <div className="text-xs text-muted-foreground">{p.customer_email}{p.customer_phone ? ` · ${p.customer_phone}` : ""}</div>
                  )}
                  {p.updated_at && (
                    <div data-testid={`pay-edited-${p.id}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-900"
                      title={`Edited by ${p.updated_by_name || "someone"} on ${fmtDate(p.updated_at)}`}>
                      <Pencil className="h-2.5 w-2.5" strokeWidth={2} />
                      edited by {p.updated_by_name || "someone"} · {fmtDate(p.updated_at)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{METHOD_LABEL[p.method] || p.method}</td>
                <td className="px-4 py-3 text-muted-foreground tabular">{p.reference_no || "-"}</td>
                <td className="px-4 py-3 text-right tabular text-primary">{fmtINR(p.amount)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button data-testid={`pay-download-${p.id}`} size="icon" variant="ghost"
                            disabled={busyId === p.id}
                            title="Download receipt (PDF)"
                            onClick={()=>onDownloadReceipt(p)}>
                      <FileDown className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button data-testid={`pay-email-${p.id}`} size="icon" variant="ghost"
                            disabled={busyId === p.id}
                            title={p.customer_email ? "Email receipt to customer" : "Customer has no email on file"}
                            onClick={()=>onEmailReceipt(p)}>
                      <Mail className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button data-testid={`pay-edit-${p.id}`} size="icon" variant="ghost"
                            title="Edit payment details"
                            onClick={()=>openEdit(p)}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button data-testid={`pay-delete-${p.id}`} size="icon" variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Delete payment & restore invoice balance"
                            onClick={()=>openDelete(p)}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                {q ? "No payments match your search." : "No payments recorded yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ---------- Edit payment ---------- */}
      <Dialog open={editOpen} onOpenChange={(v)=>{ if (!editBusy) setEditOpen(v); }}>
        <DialogContent data-testid="pay-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit payment · {editFor?.invoice_no}</DialogTitle>
            <DialogDescription>
              Correct the details below. Amount cannot be changed — to fix a wrong amount, delete this payment and re-record it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-sm text-muted-foreground">
              Amount: <span className="tabular text-foreground font-medium">{fmtINR(editFor?.amount || 0)}</span>
            </div>
            <div>
              <Label>Paid on</Label>
              <Input data-testid="pay-edit-date" type="date" value={editForm.paid_at}
                max={todayISO()}
                onChange={(e)=>setEditForm({...editForm, paid_at: e.target.value})}/>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={editForm.method} onValueChange={(v)=>setEditForm({...editForm, method: v})}>
                <SelectTrigger data-testid="pay-edit-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference no.</Label>
              <Input data-testid="pay-edit-ref" value={editForm.reference_no}
                onChange={(e)=>setEditForm({...editForm, reference_no: e.target.value})}/>
            </div>
            <div>
              <Label>Notes</Label>
              <Input data-testid="pay-edit-notes" value={editForm.notes}
                onChange={(e)=>setEditForm({...editForm, notes: e.target.value})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setEditOpen(false)} disabled={editBusy}>Cancel</Button>
            <Button data-testid="pay-edit-submit" onClick={submitEdit} disabled={editBusy}>
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Delete payment ---------- */}
      <Dialog open={delOpen} onOpenChange={(v)=>{ if (!delBusy) setDelOpen(v); }}>
        <DialogContent data-testid="pay-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete this payment?</DialogTitle>
            <DialogDescription>
              This will remove the payment and restore the invoice's outstanding balance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="tabular font-medium">{delFor?.invoice_no}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{delFor?.customer_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid on</span><span className="tabular">{fmtDate(delFor?.paid_at || delFor?.created_at)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="tabular font-medium text-primary">{fmtINR(delFor?.amount || 0)}</span></div>
            {Number(delFor?.overpayment || 0) > 0 && (
              <>
                <div className="flex justify-between pt-2 border-t border-border mt-1">
                  <span className="text-muted-foreground">Overpayment on this payment</span>
                  <span className="tabular font-medium text-amber-700">{fmtINR(delFor?.overpayment || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Customer's current credit</span>
                  <span className="tabular font-medium">{fmtINR(delFor?.customer_credit_balance || 0)}</span>
                </div>
                {Number(delFor?.customer_credit_balance || 0) < Number(delFor?.overpayment || 0) ? (
                  <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Reversing this overpayment ({fmtINR(delFor?.overpayment || 0)}) will push the customer's credit to
                    <strong className="mx-1 tabular">{fmtINR((delFor?.customer_credit_balance || 0) - (delFor?.overpayment || 0))}</strong>.
                    Some of the credit was likely already applied to a later invoice — review their account after deleting.
                  </div>
                ) : (
                  <div className="mt-1 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    After deletion the customer's credit will drop to
                    <strong className="mx-1 tabular text-foreground">{fmtINR((delFor?.customer_credit_balance || 0) - (delFor?.overpayment || 0))}</strong>.
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDelOpen(false)} disabled={delBusy}>Cancel</Button>
            <Button data-testid="pay-delete-confirm" variant="destructive" onClick={submitDelete} disabled={delBusy}>
              {delBusy ? "Deleting…" : "Delete payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
